import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { notification } from 'antd';
import { setTasks, addTask, removeTask, selectTask } from '../../store/tasksSlice';
import {
  setLoadingForType,
  setResultsForType,
  setErrorForType,
} from '../../store/searchSlice';
import {
  isSearchRequestType,
  isCreateTaskRequestType,
} from '../../shared/searchRequestTypes';
import { extractSearchResultList } from '../../utils/socketPayloads';
import { CREATE_TASK_TIMEOUT_MS, emitWhenConnected } from '../socketHelpers';

export function useTasksApi(socket) {
  const dispatch = useDispatch();

  const getTasksFromServer = useCallback(
    (limit = 50, callback) => {
      if (!socket) {
        if (callback) callback('Сокет не подключен', null);
        return;
      }

      emitWhenConnected(socket, () => {
        socket.emit('get_tasks', { limit }, (response) => {
          if (response.status === 'ok') {
            dispatch(setTasks(response.tasks));
            if (callback) callback(null, response.tasks);
          } else {
            if (callback) callback(response.message, null);
            notification.error({
              message: 'Ошибка получения задач',
              description: response.message,
            });
          }
        });
      });
    },
    [socket, dispatch]
  );

  const getRequestsForTask = useCallback(
    (taskId) => {
      if (!socket) return;

      emitWhenConnected(socket, () => {
        socket.emit('get_requests', taskId, (response) => {
          if (response.status !== 'ok') {
            notification.error({
              message: 'Ошибка при получении запросов',
              description: response.message,
            });
            return;
          }

          (response.requests || []).forEach((req) => {
            const requestType = req.request_type;
            if (!isSearchRequestType(requestType)) return;

            if (req.status === 'pending') {
              dispatch(setLoadingForType({ requestType, value: true }));
              dispatch(setErrorForType({ requestType, error: null }));
            } else if (req.status === 'completed' || req.status === 'success') {
              dispatch(setLoadingForType({ requestType, value: false }));
              dispatch(setErrorForType({ requestType, error: null }));
              const list = extractSearchResultList(req.result);
              dispatch(setResultsForType({ requestType, result: list?.length ? list : null }));
            } else if (req.status === 'error') {
              dispatch(setLoadingForType({ requestType, value: false }));
              dispatch(setErrorForType({ requestType, error: `Произошла ошибка при поиске ${requestType}` }));
            }
          });
        });
      });
    },
    [socket, dispatch]
  );

  const createNewTaskOnServer = useCallback(
    (description, requestType, callback) => {
      if (!socket) {
        if (callback) callback('Сокет не подключен', null);
        return;
      }

      if (!isCreateTaskRequestType(requestType)) {
        if (callback) callback(`Неизвестный тип запроса: ${requestType}`, null);
        return;
      }

      if (isSearchRequestType(requestType)) {
        dispatch(setResultsForType({ requestType, result: [] }));
        dispatch(setLoadingForType({ requestType, value: true }));
      }

      emitWhenConnected(socket, () => {
        let finished = false;
        const timer = setTimeout(() => {
          if (finished) return;
          finished = true;
          if (isSearchRequestType(requestType)) {
            dispatch(setLoadingForType({ requestType, value: false }));
          }
          notification.warning({
            message: 'Таймаут',
            description: 'Сервер не ответил на создание задачи вовремя',
          });
          if (callback) callback('Таймаут создания задачи', null);
        }, CREATE_TASK_TIMEOUT_MS);

        socket.emit('create_task', { description, request_type: requestType }, (response) => {
          if (finished) return;
          finished = true;
          clearTimeout(timer);
          if (response.status === 'ok') {
            dispatch(addTask(response.task));
            dispatch(selectTask(response.task));
            if (callback) callback(null, response.task);
          } else {
            if (isSearchRequestType(requestType)) {
              dispatch(setLoadingForType({ requestType, value: false }));
            }
            if (callback) callback(response.message, null);
          }
        });
      });
    },
    [socket, dispatch]
  );

  const deleteTaskOnServer = useCallback(
    (taskId, callback) => {
      if (!socket) {
        if (callback) callback('Сокет не подключен', null);
        return;
      }

      emitWhenConnected(socket, () => {
        socket.emit('delete_task', taskId, (response) => {
          if (response.status === 'ok') {
            dispatch(removeTask(taskId));
            if (callback) callback(null, response.message);
          } else if (callback) {
            callback(response.message, null);
          }
        });
      });
    },
    [socket, dispatch]
  );

  return {
    getTasksFromServer,
    getRequestsForTask,
    createNewTaskOnServer,
    deleteTaskOnServer,
  };
}
